// Community Manager Class
// Handles business logic and state management for the community section

import { Manager, logger } from '../../../core';

export class CommunityManager extends Manager {
  constructor() {
    super();

    this.isLoading = false;
  }

  async initGlobalVariables() {
    logger.log(
      { tags: 'manager|community|init', color1: 'cyan' },
      'Initializing CommunityManager global variables'
    );
  }

  async initStates() {
    logger.log(
      { tags: 'manager|community|init', color1: 'cyan' },
      'Initializing CommunityManager states'
    );
  }

  /**
   * Build query string for browse apps API
   * visibility: 'public' | 'all' | 'private' | 'needing_review' | 'not_needing_review' (admin; default public)
   * syncStatus: 'all' | 'synced' | 'neverSynced' (admin; default all)
   * teamStatus: 'all' | 'teamBased' | 'solo' (admin; default all)
   * searchBy: 'name' | 'author' (default name)
   */
  _buildBrowseQueryString({ q, tags, sort, cursor, limit, visibility, syncStatus, teamStatus, searchBy, adminView } = {}) {
    const params = new URLSearchParams();
    if (q && q.trim()) params.set('q', q.trim());
    if (tags && tags.length > 0) params.set('tags', tags.join(','));
    if (sort) params.set('sort', sort);
    if (cursor) params.set('cursor', cursor);
    if (limit != null) params.set('limit', String(limit));
    if (visibility && visibility !== 'public') params.set('visibility', visibility);
    if (syncStatus && syncStatus !== 'all') params.set('syncStatus', syncStatus);
    if (teamStatus && teamStatus !== 'all') params.set('teamStatus', teamStatus);
    if (searchBy && searchBy !== 'name') params.set('searchBy', searchBy);
    if (adminView) params.set('adminView', '1');
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }

  /**
   * Fetch browse apps from API (supporter only)
   * Supports search (q), tags, sort, cursor pagination, visibility/syncStatus/teamStatus (admin)
   * @returns {{ apps: Array, nextCursor: string|null, hasMore: boolean }}
   */
  async fetchBrowseApps({ q, tags, sort, cursor, limit = 20, visibility, syncStatus, teamStatus, searchBy, adminView } = {}) {
    const qs = this._buildBrowseQueryString({ q, tags, sort, cursor, limit, visibility, syncStatus, teamStatus, searchBy, adminView });
    const response = await fetch(`/api/community/apps${qs}`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch community apps');
    }
    return {
      apps: data.apps || [],
      nextCursor: data.nextCursor || null,
      hasMore: data.hasMore === true,
    };
  }

  async fetchAdminAppDetails(appId) {
    const response = await fetch(`/api/admin/community/apps/${appId}`, {
      method: 'GET',
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to fetch app details');
    }
    return {
      app: data.app || null,
      owner: data.owner || null,
      effectiveReview: data.effectiveReview || null,
      currentReview: data.currentReview || null,
    };
  }

  async updateUserReviewMode(userId, reviewMode) {
    const response = await fetch(`/api/admin/community/users/${userId}/review-mode`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ reviewMode }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update user review mode');
    }
    return data;
  }

  async updateAppReviewMode(appId, reviewMode) {
    const response = await fetch(`/api/admin/community/apps/${appId}/review-mode`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ reviewMode }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update app review mode');
    }
    return data;
  }

  /**
   * Approve or reject a pending community review (admin only).
   * @param {string} reviewId - Review document ID
   * @param {'approve'|'reject'} action - approve or reject
   * @param {string} [reviewNotes] - Optional notes for the decision
   * @returns {Promise<{ result: { reviewId, appId, status } }>}
   */
  async approveReview(reviewId, action, reviewNotes = null) {
    const response = await fetch(`/api/admin/community/reviews/${reviewId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ action, reviewNotes: reviewNotes || null }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to moderate review');
    }
    return data;
  }

  /**
   * Update an app's community review status and notes (admin only).
   * Used to correct mistakes (e.g. change Approved back to Deferred).
   * @param {string} appId - App ID
   * @param {'approved'|'rejected'|'deferred'} reviewStatus - New status
   * @param {string|null} [reviewNotes] - Optional message to app owner
   * @returns {Promise<{ result: { appId, reviewStatus } }>}
   */
  async updateAppReviewStatus(appId, reviewStatus, reviewNotes = null) {
    const response = await fetch(`/api/admin/community/apps/${appId}/review-status`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        reviewStatus,
        reviewNotes: reviewNotes != null ? reviewNotes : null,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || 'Failed to update review status');
    }
    return data;
  }
}

